import { useQuery } from '@apollo/client';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { ITag } from '@fluentui/react';
import { Button, Divider, makeStyles } from '@fluentui/react-components';
import moment from 'moment';
import React from 'react';
import { dividerStyles } from '../../../app/common/styles/CommonStyleObjects';
import GET_ENTITY_STATUSES_QUERY from '../../../utils/statApi/EntityStatusApi';
import IDropdownReference from '../../../utils/types/IDropdownReference';
import ProcessSearchCompanyPicker from '../../processes/processSearch/ProcessSearchCompanyPicker';
import ProcessSearchCountryPicker from '../../processes/processSearch/ProcessSearchCountryPicker';
import { multiDropdownMediumWrapper } from '../documents/CompanyDocumentStyles';
import { ICompaniesSearchState } from './CompaniesListPage';
import FilterWwAreaPicker from './FilterWwAreaPicker';
import StatUserPicker from './StatUserPicker';
import MultiSelectDropdown from '../../common/MultiSelectDropdown';
import IDropdownOption from '../../../utils/types/IDropdownOption';

interface ICompaniesListFilterProps {
  onResetFiltersClick: () => void;
  filterState: ICompaniesSearchState;
  onFieldChange: (propertyName: keyof ICompaniesSearchState, value: number[] | string[]) => void;
}
const useStyles = makeStyles({
  filterPanel: { width: '300px' },
});

const CompaniesListFilter = (props: ICompaniesListFilterProps): JSX.Element => {
  const styles = dividerStyles();
  const classes = useStyles();
  const { onResetFiltersClick, filterState, onFieldChange } = props;
  const {
    companyCodes,
    countryCodes,
    entityStatusIds,
    statutoryYearEndMonths,
    statutoryControllerIds,
    secRegionAreaCodes,
  } = filterState;
  const { loading, data } = useQuery(GET_ENTITY_STATUSES_QUERY);
  const { entityStatuses: entityStatusesSource } = !loading && data ? data : { entityStatuses: [] };
  const entityStatusOptions =
    entityStatusesSource?.map((entityStatus: IDropdownReference) => {
      return { key: entityStatus.id.toString(), text: entityStatus.fieldName };
    }) || [];
  const statutoryYearEndMonthOptions = moment.months().map((month: string, index: number) => {
    return {
      key: (index + 1).toString(),
      text: month,
    };
  });

  const onUpdateEntityStatus = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    onFieldChange('entityStatusIds' as keyof ICompaniesSearchState, itemIds);
  };

  const onUpdateStatutoryYearEndMonths = (
    _event: React.FormEvent<HTMLElement>,
    items: IDropdownOption[],
  ): void => {
    const itemIds = items.map((item) => Number(item.key));
    onFieldChange('statutoryYearEndMonths' as keyof ICompaniesSearchState, itemIds);
  };
  const onUpdateStatutoryController = (itemIds: number[]): void => {
    onFieldChange('statutoryControllerIds' as keyof ICompaniesSearchState, itemIds);
  };

  return (
    <StackItem grow disableShrink className={classes.filterPanel}>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <h2>Filter</h2>
        <Button onClick={onResetFiltersClick}>Reset Filters</Button>
      </Stack>
      <ProcessSearchCompanyPicker
        initialSelectedCodes={companyCodes}
        onCompanyUpdate={(items: ITag[]) => {
          onFieldChange(
            'companyCodes',
            items.map((item) => item.name.split(' ')[0].toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <ProcessSearchCountryPicker
        initialSelectedCodes={countryCodes}
        onCountryUpdate={(items: ITag[]) => {
          onFieldChange(
            'countryCodes',
            items.map((item) => item.key.toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <FilterWwAreaPicker
        initialSelectedCodes={secRegionAreaCodes}
        onRegionAreaUpdate={(items: ITag[]) => {
          onFieldChange(
            'secRegionAreaCodes',
            items.map((item) => item.key.toString()),
          );
        }}
      />
      <Divider className={styles.divider} />
      <MultiSelectDropdown
        label="Legal Status"
        className={multiDropdownMediumWrapper}
        onChange={onUpdateEntityStatus}
        selectedKeys={entityStatusIds.map((id) => id.toString())}
        placeholder="Select an option"
        options={entityStatusOptions}
      />
      <Divider className={styles.divider} />
      <MultiSelectDropdown
        label="Statutory Year End Month"
        className={multiDropdownMediumWrapper}
        onChange={onUpdateStatutoryYearEndMonths}
        selectedKeys={statutoryYearEndMonths.map((id) => id.toString())}
        placeholder="Select an option"
        options={statutoryYearEndMonthOptions}
      />
      <Divider className={styles.divider} />
      <StatUserPicker
        label="Statutory Controller"
        initialSelectedItems={statutoryControllerIds}
        onInputFieldUpdate={(items) => {
          onUpdateStatutoryController(items);
        }}
      />
    </StackItem>
  );
};
export default CompaniesListFilter;
